import React, { memo, useState, useRef, useMemo, useEffect } from 'react';
import CustomDialog from '../CustomDialog';
import Button from '../Button';
import PreviewImage from "./subComponents/PreviewImage";
import WrapperDraggable from "../../../shared/components/WrapperDraggable";
import { useTranslation } from 'react-i18next';
import { DialogTheme } from '../../themes/dialogTheme';
import styles from './customDialogUploadFile.module.scss';
import { getTypeFile, defaultFileInformation } from './helpers';

const CustomDialogUploadFile = ({
    onClose = () => { },
    onSubmit = () => { },
    onSubmitValidateFile = () => { },
    onChangeFile = () => { },
    onCancelAfterValidate = () => { },
    title = "",
    subTitle = "",
    validateFileBeforeUpload: _validateFileBeforeUpload = false,
    typesFile: _typesFile = getTypeFile(),
    minWidth = 1024,
    minHeigth = 768,
    maxWidth = 1024 * 3,
    maxHeigth = 768 * 3,
    submitFetchingFile,
    submitValidateFetchingFile,
    submitValidateBatchSuccess,
    downloadExample,
    itemsToLoadAfterValidating,
    showItemToLoad = false,
    type,
    ...props
}) => {
    const { t } = useTranslation(['home', 'persons']);

    const typesFile = useMemo(() => getTypeFile(_typesFile), [_typesFile]);

    const input = useRef(null);

    const [validateFileBeforeUpload, setValidateFileBeforeUpload] = useState(_validateFileBeforeUpload);

    const [notAvailable, setNotAvailable] = useState(false);

    const [file, setFile] = useState(null);

    const [fileInformation, setFileInformation] = useState({
        ...defaultFileInformation,
        name: t(defaultFileInformation.name)
    });

    const [previewImageBeforeUpload, setPreviewImageBeforeUpload] = useState(null);

    const handleSetFile = (event) => {
        changeFile(event.target.files[0]);
    }

    const showNotAvailable = () => {

        setNotAvailable(true);
        setTimeout(() => {
            setNotAvailable(false);
        }, 1000 * 5);

        reset();
    }

    const validateWidthAndHeigth = (event) => {
        if (
            event.target.naturalWidth < minWidth
            || event.target.naturalWidth > maxWidth
            || event.target.naturalHeight < minHeigth
            || event.target.naturalHeight > maxHeigth
        ) {
            showNotAvailable();
        }
    }

    const reset = () => {
        setFile(null);

        input.current.value = "";

        setFileInformation({
            ...defaultFileInformation,
            name: t(defaultFileInformation.name)
        });

        setPreviewImageBeforeUpload(null);
    }

    const changeFile = (file) => {

        onChangeFile(file);
        setValidateFileBeforeUpload(_validateFileBeforeUpload);

        if (!file) {
            return reset();
        }

        const size = Math.round(file.size / 1000);

        const isValidImage = typesFile.indexOf(file.type) > -1 && size <= 200;

        if (isValidImage) {
            setFileInformation({
                size,
                unit: "KB",
                name: file.name
            })
            setFile(file);
            setPreviewImageBeforeUpload(URL.createObjectURL(file));

        } else {
            showNotAvailable(isValidImage);
        }
    }

    const onClick = () => {
        input.current.click();
    }

    const handleOnSubmit = (file) => {

        if (!file) {
            return false;
        }

        if (validateFileBeforeUpload) {
            onSubmitValidateFile(file);
        } else {
            onSubmit(file);
        }
    }

    const handleOnClose = () => {
        if (showItemToLoad) {
            if(type === 'deregister') return onClose();
            setValidateFileBeforeUpload(_validateFileBeforeUpload);
            reset();
            onCancelAfterValidate();
        } else {
            onClose();
        }
    }

    useEffect(() => {
        if (submitValidateBatchSuccess) {
            setValidateFileBeforeUpload(!validateFileBeforeUpload);
        }
    }, [submitValidateBatchSuccess, validateFileBeforeUpload]);

    return (
        <CustomDialog
            {...props}
            dialogTitle={title}
            dialogSubTitle={subTitle}
            dialogTheme={DialogTheme}
            dialogActions={
                <>
                    {downloadExample}
                    <Button onClick={handleOnClose}>{t("home:cancel")}</Button>
                    {
                        validateFileBeforeUpload &&
                        <Button
                            onClick={handleOnSubmit.bind(this, file)}
                            loading={submitValidateFetchingFile}
                            disabled={fileInformation.size === 0}
                        >
                            {t("home:validate_file")}
                        </Button>
                    }
                    {
                        !validateFileBeforeUpload &&
                        <Button
                            onClick={handleOnSubmit.bind(this, file)}
                            loading={submitFetchingFile}
                        >
                            {t("home:submit")}
                        </Button>
                    }
                </>
            }
        >
            <div className={styles.wrapper}>

                <WrapperDraggable onDrop={changeFile} onClick={onClick} previewImage={previewImageBeforeUpload}>
                    {
                        showItemToLoad
                            ? itemsToLoadAfterValidating
                            : <PreviewImage
                                previewImageBeforeUpload={previewImageBeforeUpload}
                                fileInformation={fileInformation}
                                validateWidthAndHeigth={validateWidthAndHeigth}
                                notAvailable={notAvailable}
                                maxWidth={maxWidth}
                                maxHeigth={maxHeigth}
                                typesFile={_typesFile}
                            />
                    }
                </WrapperDraggable>

                <div className={styles.footer}>
                    <div className={styles.previewFile}>
                        {fileInformation.name}
                    </div>
                    <div className={styles.previewFile}>
                        {`${fileInformation.size} ${fileInformation.unit}`}
                    </div>
                </div>

                <input
                    type="file"
                    onChange={handleSetFile}
                    ref={input}
                    accept={typesFile}
                />
            </div>
        </CustomDialog>
    )
}

export default memo(CustomDialogUploadFile);